import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SessionInfo } from 'src/app/shared/model/register.model';
import { environment } from 'src/environments/environment';
import { Observable, tap } from 'rxjs';
import { OrderRequest } from 'src/app/shared/model/order.model';
import { Collective } from 'src/app/shared/model/user.model';
import { HashUtils } from 'src/app/shared/utils/hashUtils';
import {
  ExternalCompanyActionResponse,
  ExternalCompanyEmployeesResponse
} from 'src/app/shared/model/external-company-employee.model';

@Injectable({
  providedIn: 'root'
})
export class CoreService {
  urlCore =
    environment.env === 'dev'
      ? 'https://' + `${environment.API_CONTEXT}/core-service`
      : `${environment.API_CONTEXT}/core-service`;
  urlAdmin = this.urlCore + '/administrator';
  constructor(
    private http: HttpClient,
    private hashService: HashUtils
  ) { }

  registerUser(
    nis: string,
    password: string,
    code?: string,
    collective?: Collective
  ) {
    const bodyParams = { id: nis, password, code, collective };
    return this.http.post(`${this.urlCore}/identity/signup`, bodyParams);
  }
  // CRMSP-7485 - Acceso a MyGarage VO para jubilados
  preRegisterRetiree(
    nis: string,
    name: string,
    surname: string,
    birthDate: string,
    email: string,
    phone: string,
    collective?: Collective
  ) {
    const bodyParams = {
      id: nis,
      name,
      surname,
      birthDate,
      email,
      phone,
      collective
    };

    return this.http.post(
      `${this.urlCore}/identity/retiree/pre-register`,
      bodyParams
    );
  }

  validateCompanyCode(
    code: string
  ) {
    return this.http.post(
      `${this.urlCore}/identity/company/code`,
      {
        code
      }
    );
  }
  
  registerCompanyDigital(
    code: string,
    nis: string,
    email: string,
    name: string,
    surname: string,
    phone: string
  ) {
    const bodyParams = {
      code,
      id: nis,
      name,
      surname,
      email,
      phone
      
    };
    return this.http.post(
      `${this.urlCore}/identity/company/registerDigital`,
      bodyParams
    );
  }

  getExternalUsers(nis: string) {
    const params = new HttpParams().set('nis', nis);
    return this.http.get<ExternalCompanyEmployeesResponse>(
      `${this.urlCore}/identity/company/all`,
      { params }
    );
  }

  approveExternalUser(id: string) {
    return this.http.post<ExternalCompanyActionResponse>(
      `${this.urlCore}/identity/company/${id}/approve`,
      {}
    );
  }

  rejectExternalUser(id: string) {
    return this.http.post<ExternalCompanyActionResponse>(
      `${this.urlCore}/identity/company/${id}/reject`,
      {}
    );
  }

  deactivateExternalUser(id: string) {
    return this.http.put<ExternalCompanyActionResponse>(
      `${this.urlCore}/identity/company/${id}/deactivate`,
      {}
    );
  }
  

  confirmSignup(nis: string, password: string, confirmCode?: string) {
    const bodyParams = { id: nis, password, confirmCode };
    return this.http.post(`${this.urlCore}/identity/confirmsignup`, bodyParams);
  }

  resendConfirmationCode(nis: string) {
    const bodyParams = { id: nis };
    return this.http.post(
      `${this.urlCore}/identity/resendconfirmationcode`,
      bodyParams
    );
  }

  verifyMfa(
    nis: string,
    password: string,
    verificationCode: string,
    session: string | undefined
  ) {
    const bodyParms = { id: nis, password, verificationCode, session };
    return this.http.post(`${this.urlCore}/identity/verifyMfa`, bodyParms);
  }

  signin(
    nis: string,
    password: string,
    telephone?: string,
    collective?: Collective
  ) {
    const bodyParams = { id: nis, password, telephone, collective };
    return this.http.post<SessionInfo>(
      `${this.urlCore}/identity/signin`,
      bodyParams
    );
  }

  forgotPassword(nis: string) {
    const bodyParams = { id: nis };
    return this.http.post(
      `${this.urlCore}/identity/forgotpassword`,
      bodyParams
    );
  }

  confirmPassword(nis: string, confirmationCode: string, newPassword: string) {
    const bodyParams = { id: nis, confirmationCode, newPassword };
    return this.http.post(
      `${this.urlCore}/identity/confirmpassword`,
      bodyParams
    );
  }

  authchallenge(nis: string, verificationCode: string, session: string) {
    const bodyParams = { id: nis, verificationCode, session };
    return this.http.post<string>(
      `${this.urlCore}/identity/authchallenge`,
      bodyParams
    );
  }

  logout(token: string) {
    const bodyParams = { token };
    return this.http.post<string>(
      `${this.urlCore}/identity/signout`,
      bodyParams
    );
  }

  newOrder(orderRequest: OrderRequest) {
    const orderHash = this.hashService.createHash(JSON.stringify(orderRequest));
    const headers = {
      'Content-Type': 'application/json',
      'X-Order-Hash': orderHash
    };
    return this.http
      .post(`${this.urlCore}/orders-legacy`, orderRequest, { headers })
      .pipe(
        tap((resp) => {
          return resp;
        })
      );
  }

  verifyAuthCode(code: string) {
    const params = new HttpParams().set('authorizationCode', code);
    return this.http.get<string>(`${this.urlCore}/identity/verifyauthcode`, {
      params: params
    });
  }

  getAdministrators() {
    return this.http.get(`${this.urlAdmin}/administrators`);
  }

  addAdmin(nis: string) {
    const bodyParams = { nis };
    return this.http.post(`${this.urlAdmin}/administrator`, bodyParams);
  }
  deleteAdmin(nis: string) {
    const bodyParams = { nis };
    return this.http.delete(`${this.urlAdmin}/administrator`, {
      body: bodyParams
    });
  }

  getMaxQuota(code: string) {
    const params = new HttpParams().set('requestType', code);
    return this.http.get<number>(`${this.urlCore}/max-quota`, {
      params: params
    });
  }

  getDescOptionals(optionalCodes: string[]): Observable<string[]> {
    const bodyParams = { optionalCodes };
    return this.http.post<string[]>(
      `${this.urlCore}/optionals/descriptions`,
      bodyParams
    );
  }
}
